/**
 * My Data Component
 * Allows a contact to view and edit their own information
 */

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { startWith, forkJoin, switchMap, map } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UserRoleService } from '../services/user-role.service';
import { ContactService } from '../core/services/contact.service';
import { AddressService } from '../core/services/address.service';
import { ContactAddressService } from '../core/services/contact-address.service';
import { ContactMailPhoneService } from '../core/services/contact-mailphone.service';
import { CountryService } from '../core/services/country.service';
import { AttributeDefinitionService } from '../core/services/attribute-definition.service';
import { UserAttributeWithDefinition } from '../core/models';
import {
  ContactRead,
  ContactUpdate,
  AddressUpdate,
  ContactAddressWithDetails,
} from '../core/models';
import { ContactPhotoComponent } from './components/contact-photo.component';
import { AddressListComponent } from './components/address-list.component';
import { UserAttributesComponent } from './components/user-attributes.component';
import {
  ContactEditDialogComponent,
  ContactEditDialogData,
  ContactEditDialogResult,
} from './dialogs/contact-edit-dialog.component';
import { TranslatePipe } from '../core/features/translations';

@Component({
  selector: 'app-my-data',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ContactPhotoComponent,
    AddressListComponent,
    UserAttributesComponent,
    TranslatePipe,
  ],
  templateUrl: './my-data.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyDataComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly contactService = inject(ContactService);
  private readonly addressService = inject(AddressService);
  private readonly contactAddressService = inject(ContactAddressService);
  private readonly contactMailPhoneService = inject(ContactMailPhoneService);
  private readonly authService = inject(AuthService);
  private readonly countryService = inject(CountryService);
  private readonly attributeService = inject(AttributeDefinitionService);
  protected readonly roleService = inject(UserRoleService);

  // Signals
  readonly loading = this.contactService.loading;
  readonly error = this.contactService.error;
  readonly contact = this.contactService.currentContact;
  readonly saving = signal(false);
  readonly saveSuccess = signal(false);
  readonly saveMessage = signal<string>('');
  readonly countries = this.countryService.countries;
  readonly countryId = signal<number | null>(null);

  // ✅ Addresses from Complete Response (prevents redundant API call)
  readonly loadedAddresses = signal<ContactAddressWithDetails[]>([]);

  // Store address ID, phone mailphone ID and phone data for updates
  private addressId = signal<number | null>(null);
  private phoneMailPhoneId = signal<number | null>(null);
  private phoneData = signal<string>('');

  // Form
  contactForm!: FormGroup;

  // Track initial form values to detect changes
  private initialFormValue: Record<string, unknown> | null = null;

  // Signal to track form value changes
  private readonly formValueSignal = signal<Record<string, unknown> | null>(null);

  // Get user email from auth service
  readonly userEmail = computed(() => {
    const userInfo = this.authService.getUserInfo();
    return userInfo?.sub || userInfo?.email || null;
  });

  // Signal to check if there are any visible attributes for the current role
  readonly hasVisibleAttributes = computed(() => {
    const attributes = this.attributeService.userAttributes();
    if (attributes.length === 0) return false;

    const isStudent = this.roleService.isStudent();
    const isLecturer = this.roleService.isLecturer();

    // Filter logic same as in UserAttributesComponent
    const filtered = attributes
      .filter((group) => {
        if (isStudent && group.student_portal === false && !isLecturer) return false;
        if (isLecturer && group.teacher_portal === false && !isStudent) return false;
        return true;
      })
      .map((group) => ({
        ...group,
        children: (group.children || []).filter((child) => {
          if (isStudent && child.student_portal === true) return true;
          if (isLecturer && child.teacher_portal === true) return true;
          return false;
        }),
      }))
      .filter((group) => group.children && group.children.length > 0);

    return filtered.length > 0;
  });

  readonly hasChanges = computed(() => {
    // React to form value changes via signal
    this.formValueSignal();

    if (!this.initialFormValue || !this.contactForm) {
      return false;
    }

    const currentValue = this.contactForm.value;
    const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);

    if (hasChanged) {
      console.log('✅ Form has changes!');
    }

    return hasChanged;
  });

  ngOnInit(): void {
    this.initializeForm();
    this.loadCountries();
    this.loadContactData();
  }

  /**
   * Load countries for display
   */
  private loadCountries(): void {
    this.countryService.getCountries().subscribe();
  }

  /**
   * Initialize the form with validators
   */
  private initializeForm(): void {
    this.contactForm = this.fb.group({
      prename: ['', Validators.required],
      lastname: ['', Validators.required],
      companyname: [''],
      gender: [''],
      birthday: [''],
      nationality: [''],
      hometown: [''],
      phone: [''],
      ahv_number: [''],
    });

    // Subscribe to form changes and update signal
    this.contactForm.valueChanges.subscribe((value) => {
      this.formValueSignal.set(value);
    });
  }

  /**
   * Load current user's contact data
   */
  private loadContactData(): void {
    // Get user info from auth service
    const userInfo = this.authService.getUserInfo();

    if (!userInfo) {
      this.error.set('Kein Benutzer angemeldet');
      return;
    }

    // Get user's email from OIDC token claims
    // The 'sub' claim in ext-auth tokens contains the email
    const userEmail = userInfo.sub || userInfo.email;

    if (!userEmail) {
      this.error.set('Keine E-Mail-Adresse im Benutzerprofil gefunden');
      return;
    }

    // Find contact by email address, then load complete details
    this.contactService
      .getContactByEmail(userEmail)
      .pipe(
        switchMap((contact: ContactRead) => {
          // Load complete contact details with all related data
          return this.contactService.getContactComplete(contact.contact_id);
        })
      )
      .subscribe({
        next: (completeData: any) => {
          const contact = completeData.contact;
          const addresses = completeData.addresses || [];
          const mailphones = completeData.mailphones || [];

          // ✅ Store addresses for child component (prevents redundant /addresses API call)
          this.loadedAddresses.set(addresses);

          // Find primary address
          const primaryAddress = addresses.find((a: any) => a.is_primary_address) || addresses[0];

          // Find phone number - try primary first, then fallback to any phone
          const phoneEntry =
            mailphones.find(
              (m: any) =>
                m.contact_mailphone_data && !m.contact_mailphone_data.includes('@') && m.is_primary
            ) ||
            mailphones.find(
              (m: any) => m.contact_mailphone_data && !m.contact_mailphone_data.includes('@')
            );

          // Store address_id for updates
          if (primaryAddress?.fk_address_id) {
            this.addressId.set(primaryAddress.fk_address_id);
          }

          // Store phone mailphone_id and data for updates
          if (phoneEntry) {
            this.phoneMailPhoneId.set(phoneEntry.contact_mailphone_id || null);
            this.phoneData.set(phoneEntry.contact_mailphone_data || '');
          } else {
            this.phoneMailPhoneId.set(null);
            this.phoneData.set('');
          }

          // Store country ID from address in signal
          this.countryId.set(primaryAddress?.fk_country_id || null);

          // Build enriched contact object for display and editing
          const enrichedContact = {
            ...contact,
            // Address fields from primary address
            prename: primaryAddress?.prename || contact.prename || '',
            lastname: primaryAddress?.lastname || contact.lastname || '',
            companyname: primaryAddress?.companyname || contact.companyname || '',
            street: primaryAddress?.street || '',
            zip: primaryAddress?.zip || '',
            town: primaryAddress?.town || '',
            // Phone from mailphone
            phone: phoneEntry?.contact_mailphone_data || '',
          };

          // Update the contact signal for the view
          this.contactService.currentContact.set(enrichedContact);

          // Patch form values
          this.patchFormValues(enrichedContact);
        },
        error: (err: unknown) => {
          console.error('❌ Error loading contact data:', err);
        },
      });
  }

  /**
   * Patch form values from contact data
   */
  private patchFormValues(contact: any, updateInitialValue = true): void {
    const formValue = {
      prename: contact.prename || '',
      lastname: contact.lastname || '',
      companyname: contact.companyname || '',
      gender: contact.gender || '',
      birthday: contact.birthday || '',
      nationality: contact.nationality || '',
      hometown: contact.hometown || '',
      phone: contact.phone || '',
      ahv_number: contact.ahv || '',
    };

    this.contactForm.patchValue(formValue);

    // Store initial values for change detection
    if (updateInitialValue) {
      this.initialFormValue = { ...formValue };
      // Initialize the form value signal with current value
      this.formValueSignal.set({ ...formValue });
    }
  }

  /**
   * Navigate back to dashboard
   */
  navigateBack(): void {
    this.router.navigate(['/app/dashboard']);
  }

  /**
   * Open edit dialog for contact data
   */
  toggleEditMode(): void {
    const contact = this.contact();
    if (!contact) return;

    const dialogData: ContactEditDialogData = {
      contact: contact,
      countries: this.countries(),
    };

    const dialogRef = this.dialog.open(ContactEditDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: 'large-dialog',
      data: dialogData,
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result: ContactEditDialogResult | undefined) => {
      if (result) {
        this.saveContactFromDialog(result.formValue);
      }
    });
  }

  /**
   * Save contact data from dialog
   */
  private saveContactFromDialog(formValue: any): void {
    const contact = this.contact();
    if (!contact) {
      this.error.set('Kein Kontakt geladen');
      return;
    }

    this.saving.set(true);
    this.saveSuccess.set(false);
    this.saveMessage.set('');

    // Prepare contact update (only contact table fields)
    // ✅ Include all fields, even if empty (allows clearing values)
    const contactUpdateData: ContactUpdate = {
      gender: formValue.gender || null,
      birthday: formValue.birthday || null,
      nationality: formValue.nationality || null,
      hometown: formValue.hometown || null,
      ahv: formValue.ahv_number || null,
    };

    // Prepare address update for primary address (name fields)
    // ✅ Include all fields, even if empty
    const addressUpdateData: AddressUpdate = {
      prename: formValue.prename || null,
      lastname: formValue.lastname || null,
      companyname: formValue.companyname || null,
    };

    // Update contact, address, and phone
    const updates = [];

    // ✅ Always update contact (to ensure fields can be cleared)
    updates.push(this.contactService.updateContact(contact.contact_id, contactUpdateData));

    // ✅ Always update primary address (to ensure name fields can be cleared)
    const addrId = this.addressId();
    if (addrId) {
      updates.push(this.addressService.updateAddress(addrId, addressUpdateData));
    }

    // Handle phone number update/create/delete
    const newPhone = formValue.phone?.trim() || '';
    const oldPhone = this.phoneData();
    const phoneMailPhoneId = this.phoneMailPhoneId();

    if (newPhone !== oldPhone) {
      if (newPhone && phoneMailPhoneId) {
        // Update existing phone number
        updates.push(
          this.contactMailPhoneService.updateContactMailPhone(phoneMailPhoneId, {
            contact_mailphone_data: newPhone,
          })
        );
      } else if (newPhone && !phoneMailPhoneId) {
        // Create new phone number entry (category_id 3 = Mobiltelefon)
        updates.push(
          this.contactMailPhoneService.createContactMailPhone({
            fk_contact_id: contact.contact_id,
            fk_category_id: 3,
            contact_mailphone_data: newPhone,
            is_primary: true,
          })
        );
      } else if (!newPhone && phoneMailPhoneId) {
        // Delete phone number (empty string)
        updates.push(this.contactMailPhoneService.deleteContactMailPhone(phoneMailPhoneId));
      }
    }

    // ✅ Execute all updates (contact, address, phone if changed)
    forkJoin(updates).subscribe({
      next: () => {
        this.saving.set(false);
        this.saveSuccess.set(true);
        this.saveMessage.set('Daten erfolgreich gespeichert!');

        // Reload contact data to reflect changes
        this.loadContactData();

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.saveSuccess.set(false);
          this.saveMessage.set('');
        }, 3000);
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.saveSuccess.set(false);
        this.saveMessage.set('Fehler beim Speichern der Daten. Bitte versuchen Sie es erneut.');
        console.error('❌ Error saving data:', err);
      },
    });
  }

  /**
   * Get gender icon based on gender code
   */
  getGenderIcon(gender: string): string {
    switch (gender) {
      case 'M':
      case 'Herr':
        return '♂';
      case 'F':
      case 'Frau':
        return '♀';
      case 'N':
      case 'Divers':
        return '⚧';
      default:
        return '👤';
    }
  }

  /**
   * Get country name by ID
   */
  getCountryName(countryId: number | null): string {
    return this.countryService.getCountryNameById(countryId);
  }

  /**
   * Format date string to localized format
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Handle photo changed event
   */
  onPhotoChanged(): void {
    // Photo changed for contact
  }
}
